// FE-SCRM-050 — React Query hooks for the Prospect detail surface.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { BenefitsIntelligence } from "../BenefitsIntelligence";

import {
  acknowledgeProspectAlert,
  archiveProspect,
  createProspectContact,
  fetchProspectAlerts,
  fetchProspectBenefitsIntelligence,
  fetchProspectContacts,
  fetchProspectDetail,
  fetchProspectOpportunities,
  linkProspectToForm5500Sponsor,
  patchProspect,
  unlinkProspectFromForm5500Sponsor,
} from "./api";
import type {
  Form5500SponsorSuggestion,
  ProspectAlertListResponse,
  ProspectDetail,
  ProspectOpportunityListResponse,
  SalesContactCreateInput,
  SalesContactListResponse,
  SalesContactRow,
} from "./types";


export const PROSPECT_DETAIL_QUERY_KEY = "sales-prospect-detail" as const;
export const PROSPECT_INTELLIGENCE_QUERY_KEY =
  "sales-prospect-intelligence" as const;
export const PROSPECT_CONTACTS_QUERY_KEY =
  "sales-prospect-contacts-list" as const;
export const PROSPECT_OPPORTUNITIES_QUERY_KEY =
  "sales-prospect-opportunities" as const;
export const PROSPECT_ALERTS_QUERY_KEY =
  "sales-prospect-alerts" as const;


export function useProspectDetailQuery(
  prospect_id: string | undefined,
): UseQueryResult<ProspectDetail> {
  return useQuery({
    queryKey: [PROSPECT_DETAIL_QUERY_KEY, prospect_id],
    queryFn: ({ signal }) =>
      fetchProspectDetail(prospect_id!, { signal }),
    enabled: Boolean(prospect_id),
  });
}


export function useProspectBenefitsIntelligenceQuery(
  prospect_id: string | undefined,
): UseQueryResult<BenefitsIntelligence> {
  return useQuery({
    queryKey: [PROSPECT_INTELLIGENCE_QUERY_KEY, prospect_id],
    queryFn: ({ signal }) =>
      fetchProspectBenefitsIntelligence(prospect_id!, { signal }),
    enabled: Boolean(prospect_id),
  });
}


export function useProspectContactsQuery(
  prospect_id: string | undefined,
): UseQueryResult<SalesContactListResponse> {
  return useQuery({
    queryKey: [PROSPECT_CONTACTS_QUERY_KEY, prospect_id],
    queryFn: ({ signal }) =>
      fetchProspectContacts(prospect_id!, { signal }),
    enabled: Boolean(prospect_id),
  });
}


export function useProspectOpportunitiesQuery(
  prospect_id: string | undefined,
): UseQueryResult<ProspectOpportunityListResponse> {
  return useQuery({
    queryKey: [PROSPECT_OPPORTUNITIES_QUERY_KEY, prospect_id],
    queryFn: ({ signal }) =>
      fetchProspectOpportunities(prospect_id!, { signal }),
    enabled: Boolean(prospect_id),
  });
}


export function useProspectAlertsQuery(
  prospect_id: string | undefined,
): UseQueryResult<ProspectAlertListResponse> {
  return useQuery({
    queryKey: [PROSPECT_ALERTS_QUERY_KEY, prospect_id],
    queryFn: ({ signal }) =>
      fetchProspectAlerts(prospect_id!, { signal }),
    enabled: Boolean(prospect_id),
  });
}


export interface PatchTagsVariables {
  tags: string[];
}


export function usePatchProspectTagsMutation(
  prospect_id: string,
): UseMutationResult<ProspectDetail, Error, PatchTagsVariables> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tags }) => patchProspect(prospect_id, { tags }),
    onSuccess: (next) => {
      // Write-through so the overview card reflects the new tag list
      // without a refetch round-trip — inline edit feels instant.
      qc.setQueryData<ProspectDetail>(
        [PROSPECT_DETAIL_QUERY_KEY, prospect_id], next,
      );
    },
  });
}


export function useArchiveProspectMutation(
  prospect_id: string,
): UseMutationResult<ProspectDetail, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => archiveProspect(prospect_id),
    onSuccess: (next) => {
      qc.setQueryData<ProspectDetail>(
        [PROSPECT_DETAIL_QUERY_KEY, prospect_id], next,
      );
    },
  });
}


export function useCreateProspectContactMutation(
  prospect_id: string,
): UseMutationResult<SalesContactRow, Error, SalesContactCreateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => createProspectContact(prospect_id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [PROSPECT_CONTACTS_QUERY_KEY, prospect_id],
      });
    },
  });
}


// FE-SCRM-062 — Link / unlink the prospect to a Form 5500 sponsor.
// Both mutations invalidate the detail row (so ``form5500_sponsor_id``
// + ``fit_score`` chips refresh) and the benefits-intelligence query
// (so the panel re-fetches with the linked sponsor's data).

export function useLinkForm5500SponsorMutation(
  prospect_id: string,
): UseMutationResult<unknown, Error, Form5500SponsorSuggestion> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sponsor) =>
      linkProspectToForm5500Sponsor(prospect_id, sponsor.ein),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [PROSPECT_DETAIL_QUERY_KEY, prospect_id],
      });
      qc.invalidateQueries({
        queryKey: [PROSPECT_INTELLIGENCE_QUERY_KEY, prospect_id],
      });
    },
  });
}


export function useUnlinkForm5500SponsorMutation(
  prospect_id: string,
): UseMutationResult<unknown, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unlinkProspectFromForm5500Sponsor(prospect_id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [PROSPECT_DETAIL_QUERY_KEY, prospect_id],
      });
      qc.invalidateQueries({
        queryKey: [PROSPECT_INTELLIGENCE_QUERY_KEY, prospect_id],
      });
    },
  });
}


export function useAcknowledgeAlertMutation(
  prospect_id: string,
): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alert_id: string) => acknowledgeProspectAlert(alert_id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [PROSPECT_ALERTS_QUERY_KEY, prospect_id],
      });
      // Intelligence panel's "Active alerts" list mirrors the same
      // dataset; invalidate so it refreshes alongside.
      qc.invalidateQueries({
        queryKey: [PROSPECT_INTELLIGENCE_QUERY_KEY, prospect_id],
      });
    },
  });
}
